// The village. One shopkeeper to start with, standing outside the shop on the
// north road, with something to say and something they want.
//
// The cast costs no new art: `farmer()` frames recoloured at atlas build time
// (see NPC_PALETTES in render/extras.ts) give every villager the same hat, hair
// and walk cycle as the player, in different clothes.
import { CROPS, REQUEST_MAX_QTY, REQUEST_MIN_QTY, REQUEST_MULTIPLIER, type SeasonId, type WeatherId } from "./config.ts";
import type { Dir } from "./player.ts";
import type { World } from "./world.ts";

export interface Request {
  cropId: string;
  qty: number;
  reward: number;
  day: number;
  filled: boolean;
}

export interface Npc {
  id: string;
  name: string;
  /** palette key registered in render/extras.ts */
  look: string;
  x: number;
  y: number;
  dir: Dir;
  home: { x: number; y: number };
  moving: boolean;
  animTime: number;
  target: { x: number; y: number } | null;
  timer: number;
  /** last day the player said hello */
  greetedOn: number;
  friendship: number;
}

const GREETINGS: Record<SeasonId, string[]> = {
  spring: ["Everything's coming up green.", "Good soil year, this one.", "Plant early, harvest early."],
  summer: ["Hot one. Keep that can full.", "The corn likes this weather.", "Long days. Use them."],
  autumn: ["Pumpkins pay best now.", "Get it in before the frost.", "Smell that? That's money."],
  winter: ["Nothing grows. Go fishing.", "Mine's warmer than out here.", "Rest up. Spring's coming."],
};

const WEATHER_LINES: Partial<Record<WeatherId, string>> = {
  rain: "Rain's doing your watering for you.",
  storm: "Mind the fences in this wind.",
  snow: "Don't slip on the pond.",
  wind: "Blustery. Hold onto your hat.",
};

const SMALL_TALK = [
  "Bring me what I've asked for and I'll pay over the odds.",
  "The crate by your porch pays out at dawn, you know.",
  "Fish sell well. Nobody round here can be bothered to catch them.",
  "Heard the mine's got copper near the top.",
  "Buy the east plot when you can. Doubles your field.",
];

export class Npcs {
  readonly list: Npc[] = [];
  request: Request | null = null;
  /** whoever the player is mid-conversation with; set by the game, cleared when the box closes */
  talking: Npc | null = null;
  private seed = 4242;

  private rnd(): number {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
    return this.seed / 0x7fffffff;
  }

  spawnDefaults(world: World): void {
    this.list.length = 0;
    const shop = world.home.shop;
    this.list.push({
      id: "keeper",
      name: "Wren",
      look: "shopkeeper",
      x: shop.x + 0.5,
      y: shop.y + 1.5,
      dir: "down",
      home: { x: shop.x, y: shop.y + 1 },
      moving: false,
      animTime: 0,
      target: null,
      timer: 1.5,
      greetedOn: 0,
      friendship: 0,
    });
    this.list.push({
      id: "neighbour",
      name: "Otis",
      look: "villager",
      x: shop.x - 3.5,
      y: shop.y + 2.5,
      dir: "right",
      home: { x: shop.x - 4, y: shop.y + 2 },
      moving: false,
      animTime: 0,
      target: null,
      timer: 2.2,
      greetedOn: 0,
      friendship: 0,
    });
  }

  nearest(x: number, y: number, maxDist = 1.6): Npc | null {
    let best: Npc | null = null;
    let bestD = maxDist * maxDist;
    for (const n of this.list) {
      const d = (n.x - x) ** 2 + (n.y - y) ** 2;
      if (d < bestD) {
        bestD = d;
        best = n;
      }
    }
    return best;
  }

  update(dt: number, world: World, focus?: { x: number; y: number }): void {
    for (const n of this.list) {
      n.animTime += dt;
      // Nobody wanders off mid-sentence. They drop whatever errand they were on
      // and turn towards whoever is talking to them.
      if (n === this.talking) {
        n.target = null;
        n.moving = false;
        n.timer = 1;
        if (focus) n.dir = dirTowards(n.x, n.y, focus.x, focus.y);
        continue;
      }
      n.timer -= dt;
      if (n.target) {
        const dx = n.target.x - n.x;
        const dy = n.target.y - n.y;
        const len = Math.hypot(dx, dy);
        if (len < 0.08) {
          n.target = null;
          n.moving = false;
          n.timer = 1.5 + this.rnd() * 3;
        } else {
          const step = Math.min(len, 1.6 * dt);
          const nx = n.x + (dx / len) * step;
          const ny = n.y + (dy / len) * step;
          if (world.blocked(Math.floor(nx), Math.floor(ny))) {
            n.target = null;
            n.moving = false;
          } else {
            n.x = nx;
            n.y = ny;
            n.moving = true;
            n.dir = dirTowards(0, 0, dx, dy);
          }
        }
        continue;
      }
      if (n.timer > 0) continue;
      for (let tries = 0; tries < 6; tries++) {
        const tx = Math.round(n.home.x + (this.rnd() - 0.5) * 6);
        const ty = Math.round(n.home.y + (this.rnd() - 0.5) * 4);
        if (!world.inBounds(tx, ty) || world.blocked(tx, ty)) continue;
        n.target = { x: tx + 0.5, y: ty + 0.5 };
        break;
      }
      n.timer = 2 + this.rnd() * 3;
    }
  }

  spriteOf(n: Npc): string {
    if (n.moving) return `${n.look}_walk_${n.dir}_${Math.floor(n.animTime * 8) % 4}`;
    return `${n.look}_idle_${n.dir}_${Math.floor(n.animTime * 2) % 2}`;
  }

  /** What they say when you press E at them. */
  talk(n: Npc, day: number, season: SeasonId, weather: WeatherId): string[] {
    const lines: string[] = [];
    const first = n.greetedOn !== day;
    if (first) {
      n.greetedOn = day;
      n.friendship++;
      lines.push(`${n.name}: ${pick(GREETINGS[season], this.rnd())}`);
      const w = WEATHER_LINES[weather];
      if (w) lines.push(`${n.name}: ${w}`);
    }
    if (n.id === "keeper" && this.request && !this.request.filled) {
      const crop = CROPS.find((c) => c.id === this.request!.cropId)!;
      lines.push(`${n.name}: I'll take ${this.request.qty} ${crop.name} today — ${this.request.reward}g the lot.`);
    } else {
      lines.push(`${n.name}: ${pick(SMALL_TALK, this.rnd())}`);
    }
    return lines;
  }

  /** One "wanted" crop a day, drawn from what is actually in season. */
  rollRequest(day: number, season: SeasonId): Request {
    const inSeason = CROPS.filter((c) => c.seasons.includes(season));
    const pool = inSeason.length ? inSeason : CROPS;
    const crop = pool[Math.floor(this.rnd() * pool.length)]!;
    const qty = REQUEST_MIN_QTY + Math.floor(this.rnd() * (REQUEST_MAX_QTY - REQUEST_MIN_QTY + 1));
    this.request = {
      cropId: crop.id,
      qty,
      reward: Math.round(crop.price * qty * REQUEST_MULTIPLIER),
      day,
      filled: false,
    };
    return this.request;
  }

  toJSON(): unknown {
    return {
      request: this.request,
      friendship: this.list.map((n) => ({ id: n.id, f: n.friendship, g: n.greetedOn })),
    };
  }

  load(data: { request?: Request | null; friendship?: { id: string; f: number; g: number }[] }): void {
    this.request = data.request ?? null;
    for (const row of data.friendship ?? []) {
      const n = this.list.find((v) => v.id === row.id);
      if (n) {
        n.friendship = row.f;
        n.greetedOn = row.g;
      }
    }
  }
}

/** Which of the four walk facings points from one point at another. */
function dirTowards(fromX: number, fromY: number, toX: number, toY: number): Dir {
  const dx = toX - fromX;
  const dy = toY - fromY;
  return Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "right" : "left") : dy > 0 ? "down" : "up";
}

function pick<T>(arr: T[], roll: number): T {
  return arr[Math.floor(roll * arr.length) % arr.length]!;
}
